import { Injectable } from '@angular/core';

import { ShowcaseFixtureData } from '@cds/betting-offer/domain-specific/showcase';
import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { ShowcaseConfig } from '@frontend/sports/common/client-config-data-access';
import { assign } from 'lodash-es';
import { Observable, Subject } from 'rxjs';

import { Promotion } from '../crm-offer-data/crm-offer.model';
import { CrmPromotionMapperService } from '../crm-promotion-container/crm-promotion-mapper.service';
import { ShowcaseFixtureMapperService } from './showcase-fixture-mapper.service';
import {
    IShowcaseRequestInfo,
    Lobby,
    LobbySpecificRequest,
    ShowcaseFixtureGroup,
    ShowcaseModuleData,
    ShowcaseModuleSitecoreItem,
} from './showcase.model';

@Injectable({ providedIn: 'root' })
export class ShowcaseService {
    private selectSubject = new Subject<string>();
    private selectAlertSubject = new Subject<void>();
    private backgroundImage: Record<string, string> = {};

    readonly selectedEvent: Observable<string> = this.selectSubject.asObservable();
    readonly selectedAlertEvent = this.selectAlertSubject.asObservable();
    crmPromotions: Promotion[] = [];
    optionGroups: EventOptionGroup[] = [];
    eventSelectedFromTabBar = false;

    constructor(
        public config: ShowcaseConfig,
        private crmPromotionMapper: CrmPromotionMapperService,
        private showcaseFixtureMapperService: ShowcaseFixtureMapperService,
    ) {
        this.config.backgrounds.map((image) => {
            assign(this.backgroundImage, { [image.id]: image.imageSrc });
        });
    }

    getLobbySpecificData(data: LobbySpecificRequest): IShowcaseRequestInfo {
        const sitecoreData = this.filterData(data.lobby);

        return this.filterFixtureGroup(sitecoreData, data);
    }

    getLobbySpecificMappedData(data: LobbySpecificRequest, fixtures: ShowcaseFixtureData[]): ShowcaseModuleData {
        const sitecoreData = this.filterData(data.lobby);
        const showcaseModule = this.filterFixtureGroup(sitecoreData, data);

        return this.showcaseFixtureMapperService.mapShowcaseFixtures(fixtures, showcaseModule.sitecoreData);
    }

    select(eventId: string): void {
        this.selectSubject.next(eventId);
    }

    selectAlert(): void {
        this.selectAlertSubject.next();
    }

    promotionChanged(model: Promotion[], event?: EventModel): Promotion[] {
        if (!event || model.length === 0) {
            return [];
        }
        this.crmPromotions = this.crmPromotionMapper.getPromotionsForEvent(event, model);

        return this.crmPromotionMapper.getBannerPromotions(this.optionGroups, event, this.crmPromotions);
    }

    getBackgroundImageUrl(sportId: number): string {
        return this.backgroundImage[sportId];
    }

    getGridGroupIds(gridGroup: { [key: string]: string }): string[] {
        return Object.keys(gridGroup);
    }

    setParticipantsShirtColor(eventModel: EventModel): void {
        if (eventModel.scoreboard && eventModel.participants && eventModel.participants.length === 2) {
            const homeTeamColor = eventModel.scoreboard.colors ? eventModel.scoreboard.colors[0] : undefined;
            const awayTeamColor = eventModel.scoreboard.colors ? eventModel.scoreboard.colors[1] : undefined;
            if (eventModel.participants) {
                eventModel.participants[0].shirtColor = homeTeamColor;
                eventModel.participants[1].shirtColor = awayTeamColor;
            }
        }
    }

    private filterFixtureGroup = (showcaseModuleItem: ShowcaseModuleSitecoreItem[], data: LobbySpecificRequest): IShowcaseRequestInfo => {
        const showcaseFixturesGroup = showcaseModuleItem.map((showcaseItem) => {
            return {
                fixturesGroup: this.getLobbySpecificFixtures(showcaseItem.fixtureGroup, data),
                requestId: showcaseItem.contentSourceId,
            };
        });

        return { request: showcaseFixturesGroup, sitecoreData: showcaseModuleItem };
    };

    private getLobbySpecificFixtures(fixtureGroup: ShowcaseFixtureGroup[], data: LobbySpecificRequest): ShowcaseFixtureGroup[] {
        switch (data.lobby) {
            case Lobby.Home:
                return fixtureGroup;
            case Lobby.Sports:
                return fixtureGroup.filter((f) => data.sportId && data.sportId > 0 && f.sportId === data.sportId);
            case Lobby.Competition:
                return fixtureGroup.filter((f) => f.regionId && f.regionId > 0 && f.regionId === data.regionId && f.sportId === data.sportId);
            default:
                return [];
        }
    }

    private filterData(lobby: Lobby): ShowcaseModuleSitecoreItem[] {
        return this.config.items.filter((item) => {
            if (lobby === Lobby.Home) {
                return item.showOnHomeLobby;
            } else if (lobby === Lobby.Sports) {
                return item.showOnSportsLobby;
            } else {
                return item.showOnCompetitionLobby;
            }
        });
    }
}
